import React, {useMemo, useRef} from 'react';
import PropTypes from 'prop-types';
import {Box, RadioGroup, Text, Alert} from '@spothero/ui';
import BorderedRadio from 'common/bordered-radio';
import {Controller} from 'react-hook-form';
import {useSelector} from 'react-redux';
import {getCardInitials} from 'utils/payment-utils';
import CardSprite from 'common/card-sprite';
import {scrollToRef} from 'utils/dom-utils';
import {
    ADD_NEW_CARD_VALUE,
    CARD_DECLINE,
    CARD_FIELD,
    CARD_FORM_ID,
    NAME_FIELD,
} from './index';
import {getUserPaymentMethods} from 'store/user/user-selectors';
import {PAYPAL_WALLET_VALUE} from 'utils/user-utils';
import StripeUtils from 'utils/stripe';
import FormatUtils from 'utils/format';
import AddCard from './AddCard';

const GENERIC_ERROR = 'GENERIC_ERROR';
const CARD_DECLINE_ERROR = 'CARD_DECLINE_ERROR';
const NO_SELECTION_ERROR = 'NO_SELECTION_ERROR';
const INCOMPLETE_FIELDS_ERROR = 'INCOMPLETE_FIELDS_ERROR';

const ERROR_MAPPING = {
    [CARD_DECLINE_ERROR]: {
        title: 'There was a problem charging your payment method.',
        description: 'Please try another payment method.',
    },
    [GENERIC_ERROR]: {
        title: 'Oops, we hit a snag charging your payment method.',
        description:
            "Please try again. Don't worry, you won't be charged twice.",
    },
    [INCOMPLETE_FIELDS_ERROR]: {
        title: "Looks like you're not quite done",
        description:
            'Please fill in or correct all the required fields and try again',
    },
    [NO_SELECTION_ERROR]: {
        title: 'Please select a payment method',
    },
};

// Meant to be used with ./use-payment-form. Hook itself not in this component in case submission button or more needs to be rendered outside this component
const PaymentPicker = ({
    control,
    handleSubmit,
    setError,
    errors,
    onSubmitCard,
    onSuccess,
    // eslint-disable-next-line
    onFailure = _ => {},
}) => {
    const userPaymentMethods = useSelector(getUserPaymentMethods);
    const isAdmin = useSelector(state => state.user.data.isAdmin);
    const errorMessageRef = useRef();
    const addNewCardRef = useRef();

    const transformedPaymentMethods = useMemo(() => {
        if (userPaymentMethods.length === 0) {
            return [];
        }

        return userPaymentMethods.map(card => {
            if (card.wallet === PAYPAL_WALLET_VALUE) {
                return {
                    label: 'Paypal',
                    value: card.externalId,
                    cardTypeFormatted: 'paypal',
                };
            }

            const cardTypeFormatted = FormatUtils.slugify(card.type);

            return {
                label: (
                    <>
                        {getCardInitials(cardTypeFormatted)}
                        <sup>*</sup>
                        {card.last4}
                    </>
                ),
                value: card.externalId,
                cardTypeFormatted,
            };
        });
    }, [userPaymentMethods]);

    const handleError = response => {
        if (response.error === CARD_DECLINE) {
            setError(CARD_FIELD, {
                type: 'custom',
                message: CARD_DECLINE_ERROR,
            });
            onFailure(ERROR_MAPPING[CARD_DECLINE_ERROR].description);
        } else {
            setError(CARD_FIELD, {
                type: 'custom',
                message: GENERIC_ERROR,
            });
            onFailure(ERROR_MAPPING[GENERIC_ERROR].description);
        }
        scrollToRef(errorMessageRef);
    };

    const handleNewCardClick = () => {
        scrollToRef(addNewCardRef, 1);
    };

    const onSubmit = async ({card: cardValue, cardholderName}) => {
        // Would prefer to use react-hook-form validation for a lot of this, but need to scroll to ref complicates order of operations

        if (!cardValue) {
            setError(CARD_FIELD, {
                type: 'custom',
                message: NO_SELECTION_ERROR,
            });
            scrollToRef(errorMessageRef);
            onFailure(ERROR_MAPPING[NO_SELECTION_ERROR].description);

            return;
        }
        if (cardValue === ADD_NEW_CARD_VALUE && !cardholderName) {
            setError(NAME_FIELD, {
                type: 'custom',
                message: INCOMPLETE_FIELDS_ERROR,
            });
            scrollToRef(errorMessageRef);
            onFailure(ERROR_MAPPING[INCOMPLETE_FIELDS_ERROR].description);

            return;
        }

        let response = {success: false};

        if (cardValue === ADD_NEW_CARD_VALUE) {
            const tokenResult = await StripeUtils.createToken({
                name: cardholderName,
                isAdmin,
            });

            if (tokenResult?.success) {
                response = await onSubmitCard({
                    stripeToken: tokenResult.tokenId,
                });
            } else {
                const errorMessage =
                    tokenResult?.errorType === 'validation_error'
                        ? INCOMPLETE_FIELDS_ERROR
                        : CARD_DECLINE_ERROR;

                setError(CARD_FIELD, {
                    type: 'custom',
                    message: errorMessage,
                });
                scrollToRef(errorMessageRef);
                onFailure(ERROR_MAPPING[errorMessage].description);

                return;
            }
        } else {
            response = await onSubmitCard({cardValue});
        }

        if (response.success) {
            if (onSuccess) {
                onSuccess({response, cardValue});
            }
        } else {
            onFailure(response.error);

            handleError(response);
        }
    };

    const error = errors[NAME_FIELD]
        ? INCOMPLETE_FIELDS_ERROR
        : errors[CARD_FIELD]
        ? errors[CARD_FIELD].message
        : null;

    return (
        <Box
            data-testid="PaymentPicker"
            as="form"
            noValidate
            onSubmit={handleSubmit(onSubmit)}
            id={CARD_FORM_ID}
            width="100%"
            sx={{
                '.chakra-form-control': {paddingX: 0, marginX: 0},
                'legend.chakra-form__label': {
                    color: 'black',
                    marginBottom: 4,
                    fontSize: 'base',
                },
            }}
        >
            {transformedPaymentMethods.length > 0 ? (
                <Controller
                    name={CARD_FIELD}
                    control={control}
                    render={({field}) => (
                        <RadioGroup
                            sx={{
                                '.chakra-radio__control': {
                                    marginTop: 0.5,
                                },
                                '.chakra-radio__label': {
                                    overflow: 'hidden',
                                },
                            }}
                            marginBottom={4}
                            id={CARD_FIELD}
                            label="Choose your payment method"
                            {...field}
                        >
                            {transformedPaymentMethods.map(card => (
                                <BorderedRadio
                                    key={card.value}
                                    isSelected={field.value === card.value}
                                    value={card.value}
                                >
                                    <Box
                                        as="span"
                                        fontSize="base"
                                        display="flex"
                                        alignItems="center"
                                        marginTop={1}
                                        data-testid={`PaymentPicker-card-${card.label}`}
                                    >
                                        {card.cardTypeFormatted && (
                                            <CardSprite
                                                cardType={
                                                    card.cardTypeFormatted
                                                }
                                            />
                                        )}

                                        {card.label}
                                    </Box>
                                </BorderedRadio>
                            ))}
                            <Box
                                borderRadius="md"
                                borderWidth="1px"
                                borderColor={
                                    field.value === ADD_NEW_CARD_VALUE
                                        ? 'primary.default'
                                        : 'gray.200'
                                }
                                ref={addNewCardRef}
                                onClick={
                                    field.value !== ADD_NEW_CARD_VALUE
                                        ? handleNewCardClick
                                        : null
                                }
                            >
                                <BorderedRadio
                                    isSelected={
                                        field.value === ADD_NEW_CARD_VALUE
                                    }
                                    isParent
                                    value={ADD_NEW_CARD_VALUE}
                                >
                                    <Text data-testid="PaymentPicker-new-card">
                                        New Card
                                    </Text>
                                </BorderedRadio>
                                {field.value === ADD_NEW_CARD_VALUE && (
                                    <AddCard
                                        control={control}
                                        errors={errors}
                                    />
                                )}
                            </Box>
                        </RadioGroup>
                    )}
                />
            ) : (
                <Box borderRadius="md" borderWidth="1px" borderColor="gray.200">
                    <AddCard
                        control={control}
                        errors={errors}
                        containerStyles={{
                            borderRadius: 'md',
                            borderTopWidth: '0',
                            sx: {
                                '& > div:first-of-type': {
                                    borderTopRadius: 'md',
                                },
                            },
                        }}
                    />
                </Box>
            )}
            {error && (
                <Alert
                    ref={errorMessageRef}
                    title={ERROR_MAPPING[error].title}
                    description={ERROR_MAPPING[error].description}
                    showIcon
                    status="error"
                    size="md"
                    marginBottom={4}
                    marginTop={4}
                />
            )}
        </Box>
    );
};

PaymentPicker.propTypes = {
    control: PropTypes.object.isRequired,
    handleSubmit: PropTypes.func.isRequired,
    setError: PropTypes.func.isRequired,
    errors: PropTypes.object,
    onSubmitCard: PropTypes.func.isRequired,
    onSuccess: PropTypes.func,
    onFailure: PropTypes.func,
};

export default PaymentPicker;
